import uPlot from "uplot";
import type { ChartBuildResult, ChartSeries, YScaleMode } from "./buildTimeSeriesChart";
import { edgeClampZoomPlugin } from "../../utils/edgeClampZoomPlugin";
import { readUiVar } from "../../theme/uiTheme";

// Axis/grid colours follow the UI theme. uPlot accepts functions here and
// re-evaluates them on every draw, so a theme change only needs a redraw
// (GraphWindow/GraphWindowXY call u.redraw() on the theme event). Under the
// default Acrylic Glass theme — and in the headless report renderer — these
// resolve to the original #333 / #e0e0e0.
const axisStroke = () => readUiVar("plotAxis", "#333");
const gridStroke = () => readUiVar("plotGrid", "#e0e0e0");

const INVALID_TIGER_VALUE = 3.4028234663852886e38;

export interface XYBuilderArgs {
  width: number;
  height: number;
  series: ChartSeries[];
  data: { x: number[]; y: number[] }[];
  visibility: boolean[];
  yScaleMode: YScaleMode;
  yManual?: { min: number; max: number };
  xScaleMode?: "auto" | "manual";
  xManual?: { min: number; max: number };
}

function isValid(v: number): boolean {
  return Number.isFinite(v) && v < INVALID_TIGER_VALUE;
}

// XY renderer for graph_type 6 / 7 / 8 / 9. uPlot requires the x-axis array
// to be a single monotonically-sorted vector shared across all y-series, so
// we build one as the sorted union of every plot's x samples and align each
// plot's y values against it (nulls at positions the plot didn't contribute).
// Invalid values (NaN, Infinity, f32::MAX sentinel) are dropped so they don't
// inflate the auto-scale.
export function buildXYChart(args: XYBuilderArgs): ChartBuildResult | null {
  const { width, height, series: seriesMeta, data, visibility, yScaleMode, yManual, xScaleMode, xManual } = args;

  // Drop obviously-empty plots so the series-config + data shapes line up.
  const keepIdx: number[] = [];
  data.forEach((d, i) => {
    if (d.x?.length > 0 && d.y?.length > 0) keepIdx.push(i);
  });
  if (keepIdx.length === 0) return null;

  // Sorted union of x samples across kept plots, finite values only.
  const xSet = new Set<number>();
  for (const i of keepIdx) {
    const xs = data[i].x;
    for (const v of xs) if (isValid(v)) xSet.add(v);
  }
  if (xSet.size === 0) return null;
  const xAxis = Array.from(xSet).sort((a, b) => a - b);
  const xIndex = new Map<number, number>();
  for (let i = 0; i < xAxis.length; i++) xIndex.set(xAxis[i], i);

  // Align each kept plot's y samples onto the shared x axis.
  const alignedYs: (number | null)[][] = keepIdx.map((i) => {
    const out: (number | null)[] = new Array(xAxis.length).fill(null);
    const xs = data[i].x;
    const ys = data[i].y;
    const n = Math.min(xs.length, ys.length);
    for (let k = 0; k < n; k++) {
      const x = xs[k];
      if (!isValid(x)) continue;
      const idx = xIndex.get(x);
      if (idx === undefined) continue;
      const y = ys[k];
      out[idx] = isValid(y) ? y : null;
    }
    return out;
  });

  const aligned = [xAxis, ...alignedYs] as unknown as uPlot.AlignedData;
  const filtered = keepIdx.map((i) => data[i]);
  void filtered;

  const scales: Record<string, uPlot.Scale> = {
    x: xScaleMode === "manual" && xManual
      ? { time: false, auto: false, range: [xManual.min, xManual.max] }
      : { time: false },
  };
  if (yScaleMode === "manual" && yManual) {
    scales["y"] = { auto: false, range: [yManual.min, yManual.max] };
  }

  const axes: uPlot.Axis[] = [
    { stroke: axisStroke, grid: { stroke: gridStroke, width: 1 } },
    { stroke: axisStroke, grid: { stroke: gridStroke, width: 1 } },
  ];

  // Series config matches the kept plots in the same order as alignedYs.
  const series: uPlot.Series[] = [
    {},
    ...keepIdx.map((i) => {
      const s = seriesMeta[i];
      return {
        label: s.label,
        stroke: s.color,
        fill: s.color,
        width: 0,
        paths: () => null,
        points: { show: true, size: 5, stroke: s.color, fill: s.color },
        show: visibility[i] !== false,
      };
    }),
  ];

  const opts: uPlot.Options = {
    width,
    height,
    series,
    scales,
    axes,
    // Built-in drag is disabled; edgeClampZoomPlugin reimplements drag-to-zoom
    // so a selection dragged past a plot border clamps to the edge.
    cursor: { drag: { x: false, y: false } },
    legend: { show: false },
    plugins: [edgeClampZoomPlugin()],
  };

  return { opts, aligned };
}
